import { useEffect, useRef, useState } from 'react';
import SessionsToolbar, { defaultFilters, type SessionFilters } from '@generated/components/SessionsToolbar';
import SessionsTable, { type Session } from '@generated/components/SessionsTable';
import { listSessions } from '@generated/server/sessions';
import { getClockHoursMap } from '@generated/server/clockHours';

const PAGE_SIZE = 25;

function toIso(d?: Date) {
  if (!d) return undefined;
  return d.toISOString().slice(0, 10);
}

function friendlyError(err: unknown, fallback: string) {
  const message = err instanceof Error ? err.message : String(err);
  if (message.includes('NOT_AUTHENTICATED')) {
    return 'This directory requires you to be signed in to monday.com. Please open this link while logged in to your monday.com account.';
  }
  return fallback;
}

export default function App() {
  const [filters, setFilters] = useState<SessionFilters>(defaultFilters);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFirstLoad = useRef(true);
  const [retryToken, setRetryToken] = useState(0);
  const [clockHoursMap, setClockHoursMap] = useState<Record<string, number | null>>({});

  // Fetch the clock-hours lookup map once on mount
  useEffect(() => {
    let active = true;
    getClockHoursMap()
      .then((res) => { if (active) setClockHoursMap(res?.map ?? {}); })
      .catch((err) => console.error('Failed to load clock hours map', err));
    return () => { active = false; };
  }, []);

  const hostingSiteKey = filters.hostingSite.join(',');
  const gradeBandKey = filters.gradeBand.join(',');

  useEffect(() => {
    let active = true;
    const first = isFirstLoad.current;
    if (first) setLoading(true);
    else setRefetching(true);
    setError(null);

    listSessions({
      data: {
        search: filters.search || undefined,
        subject: filters.subject !== 'all' ? filters.subject : undefined,
        hostingSite: filters.hostingSite.length > 0 ? filters.hostingSite : undefined,
        gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand : undefined,
        dateFrom: toIso(filters.dateRange.from),
        dateTo: toIso(filters.dateRange.to),
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
        limit: PAGE_SIZE,
      },
    })
      .then((res) => {
        if (!active) return;
        setSessions(res?.items ?? []);
        setCursor(res?.cursor ?? null);
      })
      .catch((err) => {
        console.error('Failed to load sessions', err);
        if (active) setError(friendlyError(err, 'We could not load the session list. Please try again.'));
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setRefetching(false);
        isFirstLoad.current = false;
      });

    return () => {
      active = false;
    };
  }, [
    filters.search,
    filters.subject,
    hostingSiteKey,
    gradeBandKey,
    filters.dateRange.from,
    filters.dateRange.to,
    filters.sortBy,
    filters.sortDir,
    retryToken,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await listSessions({
        data: {
          search: filters.search || undefined,
          subject: filters.subject !== 'all' ? filters.subject : undefined,
          hostingSite: filters.hostingSite.length > 0 ? filters.hostingSite : undefined,
          gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand : undefined,
          dateFrom: toIso(filters.dateRange.from),
          dateTo: toIso(filters.dateRange.to),
          sortBy: filters.sortBy,
          sortDir: filters.sortDir,
          cursor,
          limit: PAGE_SIZE,
        },
      });
      setSessions((prev) => [...prev, ...(res?.items ?? [])]);
      setCursor(res?.cursor ?? null);
    } catch (err) {
      console.error('Failed to load more sessions', err);
      setError(friendlyError(err, 'We could not load more sessions. Please try again.'));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="h-1.5 w-full bg-[hsl(var(--brand-blue))]" aria-hidden="true" />
      <header className="border-b border-border bg-[hsl(var(--brand-blue))]">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-1 px-4 py-6 text-center sm:px-6">
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">AMSTI Professional Learning Opportunities</h1>
          <p className="max-w-2xl text-sm text-white/80">
            Browse scheduled professional learning sessions or filter by hosting site, grade, subject, or date range.
            <br />
            Visit{' '}
            <a
              href="https://amsti.org/aces"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-white underline underline-offset-2 hover:text-white/90"
            >
              amsti.org/aces
            </a>{' '}
            for more resources.
            <br />
            <a
              href="https://www.superform.spot-nik.com/form/68d2b6a886a368704f02ebf2"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-white underline underline-offset-2 hover:text-white/90"
            >
              Select this link to register for AMSTI Professional Learning.
            </a>
          </p>
        </div>
        <div className="h-0.5 w-full bg-[hsl(var(--brand-red))]" aria-hidden="true" />
      </header>

      <main className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:px-6">
        <SessionsToolbar filters={filters} onChange={setFilters} />

        {error ? (
          <div className="flex items-center justify-between gap-3 border border-destructive bg-[hsl(var(--tint-rose))] px-4 py-3 text-sm text-destructive">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setRetryToken((t) => t + 1)}
              className="shrink-0 font-medium underline underline-offset-2 hover:no-underline"
            >
              Try again
            </button>
          </div>
        ) : null}

        <SessionsTable
          sessions={sessions}
          loading={loading}
          refetching={refetching}
          loadingMore={loadingMore}
          hasMore={!!cursor}
          onLoadMore={handleLoadMore}
          clockHoursMap={clockHoursMap}
        />
      </main>
    </div>
  );
}
